import { useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ArrowLeft, ArrowRight, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { TIKTOK_LINKS, TikTokIcon, PAYMENT_METHODS } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  full_name: "",
  email: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
  sender_name: "",
};

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#ff1053]/70 transition-colors";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-[9px] uppercase tracking-[0.22em] text-white/45 mb-1.5">{label}</span>
      <input className={inputCls} {...props} />
    </label>
  );
}

export default function CartDrawer() {
  const { items, remove, setQty, clear, total, isCartOpen, setCartOpen } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState(EMPTY_FORM);
  const [method, setMethod] = useState("zelle");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (open) => {
    setCartOpen(open);
    if (!open) setTimeout(() => { setStep("cart"); setOrder(null); }, 300);
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${text} copied`);
  };

  const placeOrder = async () => {
    const required = ["full_name", "email", "line1", "city", "state", "zip", "sender_name"];
    if (required.some((k) => !form[k].trim())) {
      toast.error("Fill in every required field so we can ship your order.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/manual`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: {
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zip: form.zip.trim(),
        },
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty, size: i.size })),
        payment_method: method,
        sender_name: form.sender_name.trim(),
      });
      setOrder(res.data);
      clear();
      setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place the order — try again.");
    } finally {
      setLoading(false);
    }
  };

  const activeMethod = PAYMENT_METHODS.find((m) => m.id === method);

  return (
    <Sheet open={isCartOpen} onOpenChange={close}>
      <SheetContent
        data-testid="cart-drawer"
        className="glass-panel border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col [&>button]:text-white"
      >
        <SheetHeader className="px-7 pt-7 pb-5 border-b border-white/10">
          <SheetTitle className="font-display font-medium uppercase tracking-[0.2em] text-sm text-white flex items-center gap-3">
            {step !== "cart" && (
              <button data-testid="order-back-btn" onClick={() => setStep(step === "done" ? "cart" : "cart")} className="text-white/50 hover:text-white transition-colors" aria-label="Back to cart">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            {step === "cart" ? "Your Cart" : step === "form" ? "Shipping & Payment" : "Order Placed"}
          </SheetTitle>
        </SheetHeader>

        {/* STEP 1 — CART */}
        {step === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto px-7 py-6">
              {items.length === 0 ? (
                <p data-testid="cart-empty-msg" className="text-white/40 text-sm font-light mt-10 text-center">
                  Your cart is empty. The drop is waiting.
                </p>
              ) : (
                <ul className="flex flex-col gap-7">
                  {items.map((item) => (
                    <li key={`${item.lookup_key}-${item.size}`} data-testid={`cart-item-${item.lookup_key}-${item.size}`} className="flex gap-4">
                      <img src={item.image} alt={item.name} className="w-16 h-24 object-cover rounded-sm" />
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <p className="font-display font-medium text-sm">{item.name}</p>
                          <p className="font-unbounded text-xs">${item.price * item.qty}</p>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                          Size {item.size}{item.size === "2XL" ? " · incl. $30 extended size" : ""}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-3 border border-white/15 rounded-full px-2 py-1">
                            <button data-testid={`qty-minus-${item.lookup_key}-${item.size}`} onClick={() => setQty(item.lookup_key, item.size, item.qty - 1)} className="p-1 hover:text-[#ff1053] transition-colors" aria-label="Decrease quantity">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-xs w-4 text-center">{item.qty}</span>
                            <button data-testid={`qty-plus-${item.lookup_key}-${item.size}`} onClick={() => setQty(item.lookup_key, item.size, item.qty + 1)} className="p-1 hover:text-[#ff1053] transition-colors" aria-label="Increase quantity">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button data-testid={`remove-${item.lookup_key}-${item.size}`} onClick={() => remove(item.lookup_key, item.size)} className="text-white/40 hover:text-[#ff1053] transition-colors" aria-label="Remove item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-7 py-6">
                <div className="flex justify-between mb-1.5 text-sm">
                  <span className="text-white/50 uppercase tracking-[0.2em] text-[10px] pt-1">Total</span>
                  <span data-testid="cart-subtotal" className="font-unbounded">${total.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-white/35 mb-5">Free shipping on every order — pay with Zelle or Cash App.</p>
                <button
                  data-testid="checkout-btn"
                  onClick={() => setStep("form")}
                  className="w-full bg-white text-black rounded-full py-4 text-xs uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] active:scale-95"
                >
                  Place Order <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <a
                  data-testid="cart-tiktok-order-link"
                  href={TIKTOK_LINKS[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white transition-colors"
                >
                  <TikTokIcon className="w-3.5 h-3.5" /> Questions? DM {TIKTOK_LINKS[0].handle}
                </a>
              </div>
            )}
          </>
        )}

        {/* STEP 2 — SHIPPING & PAYMENT FORM */}
        {step === "form" && (
          <>
            <div className="flex-1 overflow-y-auto px-7 py-6 flex flex-col gap-5">
              <Field label="Full Name *" data-testid="order-name-input" value={form.full_name} onChange={set("full_name")} placeholder="Monica Alexander" autoComplete="name" />
              <Field label="Email *" data-testid="order-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" autoComplete="email" />
              <Field label="Phone (optional)" data-testid="order-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" autoComplete="tel" />

              <div className="pt-2 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Ship To</p>
                <div className="flex flex-col gap-4">
                  <Field label="Street Address *" data-testid="order-address-line1" value={form.line1} onChange={set("line1")} placeholder="123 N State St" autoComplete="address-line1" />
                  <Field label="Apt / Suite (optional)" data-testid="order-address-line2" value={form.line2} onChange={set("line2")} placeholder="Apt 4B" autoComplete="address-line2" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="City *" data-testid="order-city-input" value={form.city} onChange={set("city")} placeholder="Chicago" autoComplete="address-level2" />
                    <Field label="State *" data-testid="order-state-input" value={form.state} onChange={set("state")} placeholder="IL" autoComplete="address-level1" />
                  </div>
                  <Field label="ZIP Code *" data-testid="order-zip-input" value={form.zip} onChange={set("zip")} placeholder="60602" autoComplete="postal-code" />
                </div>
              </div>

              <div className="pt-2 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">How are you paying?</p>
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`pay-method-${m.id}`}
                      onClick={() => setMethod(m.id)}
                      className={`rounded-sm border px-4 py-4 text-left transition-colors duration-300 ${
                        method === m.id ? "border-[#ff1053] bg-[#ff1053]/10" : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <p className="font-display font-medium text-sm">{m.label}</p>
                      <p className="text-[10px] text-white/45 mt-1 leading-relaxed">{m.details.join(" · ")}</p>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-white/40 leading-relaxed mt-4">
                  After placing the order, send <span className="text-white font-unbounded">${total.toFixed(2)}</span> via{" "}
                  {activeMethod.label} — it goes straight to us. We'll email you the exact steps.
                </p>
              </div>

              <Field
                label="Name on your Zelle / Cash App account *"
                data-testid="sender-name-input"
                value={form.sender_name}
                onChange={set("sender_name")}
                placeholder="So we can match your payment"
              />
            </div>

            <div className="border-t border-white/10 px-7 py-6">
              <div className="flex justify-between mb-4 text-sm">
                <span className="text-white/50 uppercase tracking-[0.2em] text-[10px] pt-1">Total to send</span>
                <span data-testid="order-total" className="font-unbounded text-[#ff1053]">${total.toFixed(2)}</span>
              </div>
              <button
                data-testid="place-order-btn"
                onClick={placeOrder}
                disabled={loading}
                className="w-full bg-white text-black rounded-full py-4 text-xs uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {loading ? "Placing order…" : `Place Order — ${activeMethod.label}`}
              </button>
            </div>
          </>
        )}

        {/* STEP 3 — CONFIRMATION */}
        {step === "done" && order && (
          <div className="flex-1 overflow-y-auto px-7 py-8 flex flex-col items-center text-center">
            <CheckCircle2 className="w-11 h-11 text-[#ff1053] mb-6" />
            <p data-testid="order-success-number" className="font-unbounded text-xl mb-2">{order.order_number}</p>
            <p className="text-sm text-white/55 font-light mb-8">Your pieces are reserved. One last step:</p>

            <div className="w-full glass-panel rounded-sm p-6 text-left">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-3">Send ${order.total.toFixed(2)} via {activeMethod.label}</p>
              {activeMethod.details.map((d) => (
                <button
                  key={d}
                  data-testid={`copy-${activeMethod.id}-${d.replace(/[^a-z0-9]/gi, "")}`}
                  onClick={() => copy(d)}
                  className="w-full flex items-center justify-between gap-3 border border-white/15 rounded-sm px-4 py-3 mb-2 text-sm hover:border-[#ff1053]/70 transition-colors"
                >
                  <span>{d}</span>
                  <Copy className="w-3.5 h-3.5 text-white/40" />
                </button>
              ))}
              <p className="text-[11px] text-white/45 leading-relaxed mt-4">
                Put <span className="text-white font-semibold">{order.order_number}</span> in the payment note so we match it instantly.
                Once it lands, your order goes into production and ships to the address you gave us.
              </p>
            </div>

            <p className="text-[11px] text-white/40 mt-8">
              {order.email_status?.customer
                ? "A confirmation email with these steps is on its way to your inbox."
                : "Email confirmation is delayed — screenshot this page just in case."}
            </p>
            <button
              data-testid="order-done-btn"
              onClick={() => close(false)}
              className="mt-6 border border-white/30 rounded-full px-8 py-3 text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-white hover:text-black"
            >
              Done
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
